// widthPresets — Modern 太さプリセットの純ロジック
// (docs/_wip/modern-layout.md §9.11 太さプリセット、consult 2026-09-29)。
//
// 背景: feedback「Modern にも Classic みたいなペンの太さプリセットが 3 つくらい
// あったらいい」。競合調査 (CSP/SAI/Procreate/MediBang/Krita/Drawpile) の結果、
// 「スライダで登録値が変わる」上書き型は Drawpile のみで主流は **明示登録型**
// (Procreate/MediBang 型) だったため、Modern はこちらを採用する
// (§9.11 決定1)。Classic の 6 cell 記憶スロット (スライダで**上書き**) は
// 据え置き (§9.11 決定4、旧 Java `selPenWidth`/`penWidths` 踏襲、別機構)。
//
// この純ロジックは「登録リストの正規化 (clamp・重複除去・昇順・上限個数)」と
// 「`[`/`]` 段階増減の刻み幅」だけを担う。UI は `ModernWidthPresets.tsx`、永続化は
// `infrastructure/widthPresetPreference.ts` (本ファイルを import して正規化を再利用する
// — `modernWorkspacePreference.ts` が `workspaceLayout.ts` を再利用するのと同じ分担)。

import { MAX_PEN_WIDTH } from '../classic/penWidthSlider.js';

/**
 * 既定プリセット (§9.11 細目「初期値: 3 / 10 / 24px (要実機調整)」)。
 * 配列は `normalizeWidthPresets` を素通しできる形 (昇順・重複無し) で持つ。
 */
export const DEFAULT_WIDTH_PRESETS: readonly number[] = [3, 10, 24];

/**
 * 登録できる最大個数 (§9.11 細目「既定 3 個、『+』で追加して最大 5 個 (パネル幅
 * 244px に収まる範囲)」)。
 */
export const MAX_WIDTH_PRESETS = 5;

/**
 * 最低保持数。全部消せると「太さプリセット」自体が空になり復帰手段が無くなるため、
 * 最後の 1 個は削除できない (§9.11 細目「空にならない配慮は判断で」に対する判断)。
 */
export const MIN_WIDTH_PRESETS = 1;

/** 太さを 1..MAX_PEN_WIDTH に丸めて clamp する (スライダの値域と揃える)。 */
export const clampPenWidth = (w: number): number =>
  Math.min(MAX_PEN_WIDTH, Math.max(1, Math.round(w)));

/**
 * プリセット配列を正規化する: 各値を clamp、重複除去、昇順ソート、上限個数で
 * 切り詰め (§9.11 細目「同値は重複登録しない、昇順表示」)。保存済み JSON の検証・
 * 組込 default の整形の両方から使う共通経路。
 */
export const normalizeWidthPresets = (presets: readonly number[]): readonly number[] => {
  const unique = Array.from(new Set(presets.map(clampPenWidth)));
  unique.sort((a, b) => a - b);
  return unique.slice(0, MAX_WIDTH_PRESETS);
};

/**
 * 現在の太さを登録に追加する (§9.11 決定2「登録は見える『+』ボタンで現在値を追加」)。
 * 既に同値が登録済み、または上限到達なら変更しない (呼び出し側 UI は上限時に「+」を
 * 無効化するので通常は到達しないが、保険として no-op で安全に倒す)。
 */
export const addWidthPreset = (presets: readonly number[], width: number): readonly number[] => {
  const w = clampPenWidth(width);
  if (presets.includes(w)) return presets;
  if (presets.length >= MAX_WIDTH_PRESETS) return presets;
  return normalizeWidthPresets([...presets, w]);
};

/**
 * プリセットを削除する。最後の 1 個 (`MIN_WIDTH_PRESETS` 以下) は消せない
 * (= 空にしない配慮)。該当値が無ければ変更しない。
 */
export const removeWidthPreset = (presets: readonly number[], width: number): readonly number[] => {
  if (presets.length <= MIN_WIDTH_PRESETS) return presets;
  const w = clampPenWidth(width);
  const next = presets.filter((p) => p !== w);
  return next.length === presets.length ? presets : next;
};

/** 登録が既定 (`DEFAULT_WIDTH_PRESETS`) と同じか (= RESET を押しても変化が無い)。 */
export const isDefaultWidthPresets = (presets: readonly number[]): boolean =>
  presets.length === DEFAULT_WIDTH_PRESETS.length &&
  presets.every((w, i) => w === DEFAULT_WIDTH_PRESETS[i]);

/** JSON 由来の任意値が「有限数値の配列」か (= 保存データの型チェック)。 */
export const isFiniteNumberArray = (v: unknown): v is number[] =>
  Array.isArray(v) && v.every((x) => typeof x === 'number' && Number.isFinite(x));

// ── `[`/`]` 段階増減 (§9.11 決定3) ──────────────────────────────────────────
//
// 「太さに応じた段階 (例: 小さいうちは 1px、大きくなると粗く) を妥当に決める」の
// 実装。しきい値は実機調整余地のある初期値 — CSP/Photoshop 等の bracket key も
// 太さに応じて非線形に刻む慣習を踏襲する。刻み幅は **現在値の帯**で決まる (= 増加/
// 減少どちら向きでも同じ規則。帯境界をまたぐ 1 手だけ非対称になり得るが、境界跨ぎは
// 稀・実害が小さいと判断した)。

/** 太さの帯ごとの刻み幅 (`below` 未満ならその `step`、昇順で判定)。 */
const WIDTH_STEP_BANDS: ReadonlyArray<{ readonly below: number; readonly step: number }> = [
  { below: 10, step: 1 },
  { below: 30, step: 2 },
  { below: Number.POSITIVE_INFINITY, step: 5 },
];

/** 現在の太さに対する `[`/`]` 1 回あたりの増減幅。 */
export const widthStepSize = (width: number): number => {
  for (const band of WIDTH_STEP_BANDS) {
    if (width < band.below) return band.step;
  }
  // 最後の帯が below=Infinity なので通常は上で return する (= ここは型の網羅性のためだけ)。
  return WIDTH_STEP_BANDS[WIDTH_STEP_BANDS.length - 1]?.step ?? 5;
};

/**
 * `[`/`]` 1 押しぶんの太さ増減を解決する (1..MAX_PEN_WIDTH にクランプ)。
 * @param direction `1` = 太く (`]`)、`-1` = 細く (`[`)。
 */
export const stepPenWidth = (current: number, direction: 1 | -1): number =>
  clampPenWidth(current + direction * widthStepSize(current));
